import { CheckCircle, XCircle, AlertCircle, Upload } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Renders the post-import results pane (success counts, error list, action buttons).
 * Extracted from BulkImportPage so the parent can stay focused on file/upload state.
 */
export default function BulkImportResults({ importResult, onReset, onViewListings }) {
  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="p-6 rounded-lg bg-card border border-border/40">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          {importResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          Import {importResult.success ? 'Complete' : 'Failed'}
        </h2>

        {importResult.success && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {importResult.imported_count}
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">
                Successfully Imported
              </div>
            </div>

            <div className="p-4 rounded-lg bg-secondary/50">
              <div className="text-2xl font-bold">
                {importResult.total_rows || 0}
              </div>
              <div className="text-sm text-muted-foreground">
                Total Rows Processed
              </div>
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {importResult.errors.length}
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  Errors
                </div>
              </div>
            )}
          </div>
        )}

        {/* Errors List */}
        {importResult.errors && importResult.errors.length > 0 && (
          <div className="mt-4">
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-500" />
              Errors Encountered
            </h3>
            <div className="p-4 rounded-lg bg-secondary/30 max-h-64 overflow-y-auto">
              <ul className="space-y-2 text-sm">
                {importResult.errors.map((error, index) => (
                  <li key={`error-${error.substring(0, 30)}-${index}`} className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-red-500">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Fix these errors in your CSV and try importing again
            </p>
          </div>
        )}

        {importResult.error && (
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
            <p className="text-red-600 dark:text-red-400">{importResult.error}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={onReset} variant="outline">
          <Upload className="w-4 h-4 mr-2" />
          Import Another File
        </Button>

        {importResult.imported_count > 0 && (
          <Button onClick={onViewListings} className="btn-gold">
            View My Listings
          </Button>
        )}
      </div>
    </div>
  );
}
